import {
  Button,
  Caption,
  Dropdown,
  useActiveModifiers,
  useDayPicker,
  useNavigation,
  type CustomComponents
} from 'react-day-picker';
import clsx from 'clsx';
import { setMonth } from 'date-fns';
import { Chevron } from '../../chevron/chevron';
import styles from '../datepicker.module.less';

const components: CustomComponents = {
  IconDropdown: () => <Chevron className={styles.chevron} />,
  Caption: (props) => (
    <>
      <svg
        aria-hidden="true"
        className={styles.arrow}
        focusable="false"
        viewBox="0 0 30 20"
      >
        <path d="M-9.17 21.24V17H3.42c.67 0 1.3-.33 1.67-.88l8.24-12.24a2.03 2.03 0 0 1 3.34 0l8.24 12.24c.37.55 1 .88 1.67.88h14.37v4.12" />
      </svg>
      <Caption {...props} />
    </>
  )
};

export const types: Record<string, CustomComponents & { format: string }> = {
  date: {
    ...components,
    format: 'dd.MM.yyyy'
  },
  month: {
    ...components,
    format: 'MMMM yyyy',
    Dropdown: (props) =>
      props.name === 'months' ? null : <Dropdown {...props} />, // Hide month dropdown
    Row: () => <></>, // Hide original days
    Head: () => {
      const { classNames, formatters, locale, onDayClick } = useDayPicker();
      const { currentMonth, goToMonth } = useNavigation();
      const months = Array.from(Array(12), (_, m) => setMonth(currentMonth, m));

      return (
        <tbody className={clsx(classNames.tbody, styles.month)}>
          {months.map(function Month(date, index) {
            const modifiers = useActiveModifiers(date);
            const classes = clsx(classNames.day, {
              [classNames.day_today]: modifiers.today
            });

            return (
              <tr key={index} className={classNames.row}>
                <td className={classNames.cell}>
                  <Button
                    aria-selected={modifiers.selected}
                    className={classes}
                    disabled={modifiers.disabled}
                    onClick={(e) => onDayClick?.(date, modifiers, e)}
                    onKeyDown={(event) => onKeyDown(event, date, goToMonth)}
                    title={`${date.getFullYear()}`}
                    type="button"
                  >
                    {formatters.formatMonthCaption(date, { locale })}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      );
    }
  }
};

const onKeyDown = (
  event: React.KeyboardEvent<HTMLButtonElement>,
  month: Date,
  goToMonth: (month: Date) => void
) => {
  if (event.key.startsWith('Arrow')) {
    event.preventDefault(); // Prevent scroll
    const container = event.currentTarget.closest(`.${styles.month}`);
    const direction = event.key.match(/Arrow(Up|Left)/) ? -1 : 1;
    const months = getMonthButtons(container);
    const moveTo = months[months.indexOf(event.currentTarget) + direction];

    moveTo?.focus();
    if (!moveTo) {
      goToMonth(setMonth(month, month.getMonth() + direction)); // No next/prev month means we need to change year
      setTimeout(() => {
        const months = getMonthButtons(container);
        const moveTo = months[direction === 1 ? 0 : months.length - 1];
        moveTo?.focus();
      }); // Let React render new months before moving focus
    }
  }
};

const getMonthButtons = (container: Element | null): HTMLButtonElement[] =>
  Array.from(container?.querySelectorAll('button:enabled') || []);
